<template>
  <div class="manage-formulation-page">
    <vx-card >

      <ValidationObserver ref="form" v-slot="{ handleSubmit }">
        <form id="formulation_general_details_section" @submit.prevent="handleSubmit(onclickFormulationSaveConfirmation)"
              autocomplete="off">
          <div id="formPrintDiv">
            <vs-alert v-if="formulation.is_archived" color="danger" class="mb-4" icon-pack="feather" icon="icon-archive">
              <div class="flex items-center flex-wrap">
                <span class="mr-4">
                  This formulation is archived <span v-if="formulation.archived_at">(Archived At: {{ __dateTimeFormatter(formulation.archived_at) }})</span>.
                </span>
                <vs-button @click.prevent="openFormulationUnArchiveConfirm" size="small" color="dark" class="my-1">
                  Un-archive
                </vs-button>
              </div>
            </vs-alert>
            <vx-card noShadow cardBorder>
              <div class="vx-row mb-3" >
            <div class="vx-col md:w-1/5 w-full md:mb-0">
              <ValidationProvider vid="formulation_code" name="formulation_code" rules=""
                                  v-slot="{ errors }">
                <small class="label">Formulation code</small>
                <vx-input-group>
                  <vs-input v-model="formulation.formulation_code" name="formulation_code" class="w-full" label=""
                            readonly
                            :danger="!!errors[0]"
                            :danger-text="errors[0]" val-icon-danger="icon-x" val-icon-pack="feather"/>
                  <template slot="append">
                    <div style="cursor: pointer" @click="onClickCopyFormulationName(formulation.formulation_code)" class="prepend-text btn-addon">
                      <vs-button style="border-radius: 0" icon-pack="feather" icon="icon-copy" type="border"
                                 color="primary"></vs-button>
                    </div>
                  </template>
                </vx-input-group>
              </ValidationProvider>
            </div>
            <div class="vx-col md:w-1/5 w-full md:mb-0">
              <ValidationProvider vid="name" name="name" rules=""
                                  v-slot="{ errors }">
                <small class="label">Product Name</small>
                <vs-input v-model="formulation.name" name="name" class="w-full" label=""
                          :danger="!!errors[0]" :disabled="!$canAll(formulationPermissions)"
                          :danger-text="errors[0]" val-icon-danger="icon-x" val-icon-pack="feather"/>
              </ValidationProvider>
            </div>
            <div class="vx-col md:w-1/5 w-full md:mb-0">
              <ValidationProvider vid="legal_name" name="legal_name" rules="required"
                                  v-slot="{ errors }">
                <small class="label">Legal name <span class="text-danger font-bold">*</span></small>
                <vs-input v-model="formulation.legal_name" name="legal_name" class="w-full" label="" 
                          :danger="!!errors[0]" :disabled="!$canAny(formulationPermissions.concat(['qc-elements-edit']))"
                          :danger-text="errors[0]" val-icon-danger="icon-x" val-icon-pack="feather"/>
              </ValidationProvider>
            </div>
            <div class="vx-col md:w-1/5 w-full md:mb-0">
              <ValidationProvider vid="alternative_legal_name" name="alternative_legal_name" rules=""
                                  v-slot="{ errors }">
                <small class="label">Alternative Legal name</small>
                <vs-input v-model="formulation.alternative_legal_name" name="alternative_legal_name" class="w-full" label="" 
                          :danger="!!errors[0]" :disabled="!$canAny(formulationPermissions.concat(['qc-elements-edit']))"
                          :danger-text="errors[0]" val-icon-danger="icon-x" val-icon-pack="feather"/>
              </ValidationProvider>
            </div>
            <div class="vx-col md:w-1/5 w-full md:mb-0" v-if="$canAll(['view-sage-nominal-code'])">
              <ValidationProvider vid="nominal_code" name="nominal_code" rules="required"
                                  v-slot="{ errors }">
                <small class="label">Nominal Code<span class="text-danger font-bold">*</span></small>
                <v-select :clearable="false" name="nominal_code" placeholder="" v-on:input="onTypeForm"
                          label="name" v-model="formulation.sage_nominal_code_id" :reduce="option => option.id"
                          :disabled="!$canAll(formulationPermissions)"
                          :options="sage_nominal_codes" :dir="$vs.rtl ? 'rtl' : 'ltr'"/>
                <span class="text-danger text-sm">{{ errors[0] }}</span>
              </ValidationProvider>
            </div>
          </div>
          <div class="vx-row mb-3">
            <div class="vx-col md:w-1/4 w-full">
              <ValidationProvider vid="customer_project_brief_id" name="customer_project_brief_id" rules="required"
                                  v-slot="{ errors }">
                <small class="label">Formulation type <span class="text-danger font-bold">*</span></small>
                <v-select :clearable="false" name="formulation_type" v-model="formulation.formulation_type"
                          :options="['powder', 'liquid', 'spray dry powder', 'fruit']" :disabled="!$canAny(formulationPermissions.concat(['qc-elements-edit']))"
                          :dir="$vs.rtl ? 'rtl' : 'ltr'"/>
                <span class="text-danger text-sm">{{ errors[0] }}</span>
              </ValidationProvider>
            </div>
            <div class="vx-col md:w-1/4 w-full md:mb-0">
              <ValidationProvider vid="batch_weight" name="batch_weight" rules="required"
                                  v-slot="{ errors }">
                <small class="label">Batch weight (KG) <span class="text-danger font-bold">*</span></small>
                <vs-input v-model="formulation.batch_weight" name="batch_weight" class="w-full" label=""
                          :danger="!!errors[0]" :disabled="!$canAll(formulationPermissions)"
                          :danger-text="errors[0]" val-icon-danger="icon-x" val-icon-pack="feather"/>
              </ValidationProvider>
            </div>
            <div class="vx-col md:w-1/4 w-full md:mb-0">
              <ValidationProvider vid="status_name" name="status_name" rules=""
                                  v-slot="{ errors }">
                <small class="label">FG Status</small>
                <vs-input v-model="formulation.status.name + '(' + formulation.status.description + ')'"
                          name="status_name" class="w-full" label=""
                          :danger="!!errors[0]" disabled
                          :danger-text="errors[0]" val-icon-danger="icon-x" val-icon-pack="feather"/>
              </ValidationProvider>
            </div>
            <div class="vx-col md:w-1/4 w-full md:mb-0">
              <ValidationProvider vid="qa_status_name" name="qa_status_name" rules=""
                                  v-slot="{ errors }">
                <small class="label">QA Status</small>
                <vs-input v-model="formulation.qa_status.name" name="qa_status_name" class="w-full" label=""
                          :danger="!!errors[0]" disabled
                          :danger-text="errors[0]" val-icon-danger="icon-x" val-icon-pack="feather"/>
              </ValidationProvider>
            </div>
          </div>
          <div class="vx-row mb-3">
            <!-- <div class="vx-col md:w-1/4 w-full md:mb-0">
              <ValidationProvider vid="classification" name="classification" rules=""
                                  v-slot="{ errors }">
                <small class="label">Classification</small>
                <v-select :clearable="true" name="classification" v-model="formulation.classification"
                          :options="['artificial', 'natural']" :disabled="!$canAny(formulationPermissions)"
                          :dir="$vs.rtl ? 'rtl' : 'ltr'"/>
                <span class="text-danger text-sm">{{ errors[0] }}</span>
              </ValidationProvider>
            </div> -->
            <div class="vx-col md:w-1/4 w-full md:mb-0" v-if="isShowBaseLine">
              <ValidationProvider vid="dev_base_line" name="dev_base_line" rules=""
                                  v-slot="{ errors }">
                <small class="label">Dev Baseline</small>
                <vx-input-group>
                  <template slot="prepend">
                    <div style="cursor: pointer" @click="onClickViewReferenceFormula" class="prepend-text btn-addon">
                      <vs-button style="border-radius: 0" icon-pack="feather" icon="icon-eye" type="border"
                                 color="primary"></vs-button>
                    </div>
                  </template>
                  <vs-input disabled
                            v-model="formulation.reference_formulation.formulation_code"/>
                </vx-input-group>
              </ValidationProvider>
            </div>
            <div class="vx-col md:w-1/4 w-full md:mb-0">
              <small class="label">RM Code Paired</small>
              <div class="flex">
                <div v-if="selectedRmCodePaired" style="cursor: pointer" @click="onClickViewRmCodePaired" class="prepend-text btn-addon">
                  <vs-button style="border-radius: 0" icon-pack="feather" icon="icon-eye" type="border" color="primary"></vs-button>
                </div>
                <v-select
                  class="flex-1"
                  style="min-width: 0"
                  :clearable="true"
                  v-model="selectedRmCodePaired"
                  :options="productionCodes"
                  :getOptionLabel="option => option.category ? `${option.category.prefix} - ${option.company_product_code}` : option.company_product_code"
                  :filterable="false"
                  placeholder="Search..."
                  @search="fetchProductionCodes"
                  :disabled="!$canAny(formulationPermissions)"
                  :dir="$vs.rtl ? 'rtl' : 'ltr'"
                />
              </div>
            </div>
            <div class="vx-col md:w-1/4 w-full md:mb-0" v-if="isShowCopiedFormulation">
              <ValidationProvider vid="dev_base_line" name="dev_base_line" rules=""
                                  v-slot="{ errors }">
                <small class="label">Copied From</small>
                <vx-input-group>
                  <template slot="prepend">
                    <div style="cursor: pointer" @click="onClickViewReferenceFormula" class="prepend-text btn-addon">
                      <vs-button style="border-radius: 0" icon-pack="feather" icon="icon-eye" type="border"
                                 color="primary"></vs-button>
                    </div>
                  </template>
                  <vs-input disabled
                            v-model="formulation.reference_formulation.formulation_code"/>
                </vx-input-group>
              </ValidationProvider>
            </div>
            <div class="vx-col md:w-1/4 w-full md:mb-0" v-if="formulation.formulation_type === 'liquid'">
              <ValidationProvider vid="is_thick_liquid" name="is_thick_liquid" rules="required"
                                  v-slot="{ errors }">
                <small class="label">Is thick liquid?</small>
                <vs-switch v-model="formulation.is_thick_liquid" icon-pack="feather" vs-icon-on="icon-check-circle" vs-icon-off="icon-slash" class="mt-2 w-16"
                                     :disabled="!$canAny(formulationPermissions)" v-on:input="onTypeForm">
                            <span slot="on">Yes</span>
                            <span slot="off">No</span>
                </vs-switch>
              </ValidationProvider>
            </div>
            <div class="vx-col md:w-1/4 w-full md:mb-0">
              <ValidationProvider vid="sage_department_id" name="sage_department_id" rules=""
                                  v-slot="{ errors }">
                <small class="label">Sage Department (Bypass in sales order invoice)</small>
                <v-select :clearable="true" name="sage_department_id" placeholder="" v-on:input="onTypeForm"
                          label="name" v-model="formulation.sage_department_id" :reduce="option => option.id"
                          :disabled="!$canAll(formulationPermissions)"
                          :options="sage_departments" :dir="$vs.rtl ? 'rtl' : 'ltr'"/>
                <span class="text-danger text-sm">{{ errors[0] }}</span>
              </ValidationProvider>
            </div>
            <div class="vx-col md:w-1/4 w-full md:mb-0" v-if="formulation.is_archived">
              <ValidationProvider vid="archived_at" name="archived_at" rules="" v-slot="{ errors }">
                <small class="label">Archived At</small>
                <vs-input :value="__dateTimeFormatter(formulation.archived_at)" name="archived_at" class="w-full" label=""
                          disabled />
              </ValidationProvider>
            </div>
          </div>
          <div class="vx-row mb-3">
            <div class="vx-col md:w-1/4 w-full md:mb-0">
              <ValidationProvider vid="halal" name="halal" rules=""
                                  v-slot="{ errors }">
                <small class="label">Halal</small>
                <v-select name="halal" v-model="formulation.halal" label="key" :reduce="option => option.value"
                          :options="dropdown_options" :disabled="!$canAll(['qc-elements-edit'])"
                          :dir="$vs.rtl ? 'rtl' : 'ltr'"/>
                <span class="text-danger text-sm">{{ errors[0] }}</span>
              </ValidationProvider>
            </div>
            <div class="vx-col md:w-1/4 w-full md:mb-0">
              <ValidationProvider vid="kosher" name="kosher" rules=""
                                  v-slot="{ errors }">
                <small class="label">Kosher</small>
                <v-select name="kosher" v-model="formulation.kosher" label="key" :reduce="option => option.value"
                          :options="dropdown_options" :disabled="!$canAll(['qc-elements-edit'])"
                          :dir="$vs.rtl ? 'rtl' : 'ltr'"/>
                <span class="text-danger text-sm">{{ errors[0] }}</span>
              </ValidationProvider>
            </div>
            <div class="vx-col md:w-1/4 w-full md:mb-0">
              <ValidationProvider vid="organic" name="organic" rules=""
                                  v-slot="{ errors }">
                <small class="label">Organic</small>
                <v-select name="organic" v-model="formulation.organic" label="key" :reduce="option => option.value"
                          :options="dropdown_options" :disabled="!$canAll(['qc-elements-edit'])"
                          :dir="$vs.rtl ? 'rtl' : 'ltr'"/>
                <span class="text-danger text-sm">{{ errors[0] }}</span>
              </ValidationProvider>
            </div>
            <div class="vx-col md:w-1/4 w-full md:mb-0">
              <ValidationProvider vid="vegan" name="vegan" rules=""
                                  v-slot="{ errors }">
                <small class="label">Vegan</small>
                <v-select name="vegan" v-model="formulation.vegan" label="key" :reduce="option => option.value"
                          :options="dropdown_options_vegan" :disabled="!$canAll(['qc-elements-edit'])"
                          :dir="$vs.rtl ? 'rtl' : 'ltr'"/>
                <span class="text-danger text-sm">{{ errors[0] }}</span>
              </ValidationProvider>
            </div>
          </div>
          <div class="vx-row mb-3">
            <div class="vx-col w-full md:mb-0">
              <ValidationProvider vid="npd_notes" name="npd_notes" rules=""
                                  v-slot="{ errors }">
                <small class="label">NPD Description</small>
                <vs-textarea v-model="formulation.npd_notes" name="npd_notes" :disabled="!$canAll(formulationPermissions)"
                          class="w-full" rows="3" />
                <span class="text-danger text-sm">{{ errors[0] }}</span>
              </ValidationProvider>
            </div>
          </div>
          <div class="flex flex-wrap items-center justify-end mt-8">
            <vs-button  v-if="$canAll(formulationPermissions)" class="ml-auto mt-2" button="submit" icon-pack="feather" icon="icon-save">Save changes</vs-button>
          </div>
            </vx-card>

          </div>

        </form>
      </ValidationObserver>

      <span class="mt-4">
      <div class="vx-row mt-8">

        <div class="vx-col w-full md:w-1/2">
        </div>

        <div class="vx-col w-full md:w-1/2" id="formButtonSetDiv">
              <div class="btn-group float-right">
                <vs-button v-can="'qc-elements-edit'"
                           @click.stop="onClickStartQcTest"
                           v-if="formulation.qa_status_id === 2"
                           size="small" class="mr-2"> Start QC test </vs-button>

                <vs-button @click="onClickFormulationApproveConfirm"
                           v-if="approveButtonValidations && $canAll(formulationPermissions)"
                           size="small">Create production formulation</vs-button>

                <vs-button @click="openFormulationSuspendConfirm"
                           v-if="formulation.status_id === 3 && $canAny(formulationPermissions.concat([ 'qc-elements-edit' ]))" size="small"
                           color="warning">Suspend</vs-button>

                <vs-button @click="openFormulationReInstateConfirm"
                           v-if="reInstateButtonValidations && $canAny([ 'qc-elements-edit' ])" size="small"
                           color="dark">Re-Instate</vs-button>

                <!--                <vs-button @click="openFormulationChangeToDevConfirm" v-if="formulation.status_id === 2" size="small" color="#194350">Change to dev</vs-button>-->

                <vs-button @click="openFormulationQaSubmissionConfirm"
                           v-if="formulation.qa_status_id === 1 && $canAny([ 'qc-elements-edit' ])"
                           size="small" color="#28b5b5">QA submission</vs-button>

                <vs-button @click="openFormulationArchiveConfirm"
                           v-if="!formulation.is_archived && formulation.status && formulation.status.name === 'Development'"
                           size="small"
                           color="danger" class="ml-2">Archive</vs-button>

                <!--                <vs-button @click="openFormulationQaApprove" v-if="formulation.qa_status_id === 2 || formulation.qa_status_id === 3" :disabled="formulation.formulation_q_c_tests.length === 0" size="small" color="#8fd9a8">QA approve</vs-button>-->
                <!--                <vs-button @click="openFormulationQaReject" v-if="formulation.qa_status_id === 2" size="small" :disabled="formulation.formulation_q_c_tests.length === 0" color="#f05945">QA reject</vs-button>-->

                <vs-button @click="onClickSamplePlay"
                           v-if="$canAll(formulationPermissions)"
                           type="border" size="small" color="warning" class="ml-2">Model Quantity</vs-button>

                <vs-button @click="onClickFormulationReplica"
                           v-if="$canAll(formulationPermissions)"
                           type="border" size="small" color="dark" class="ml-2">Copy formulation</vs-button>

                <vs-button @click="printDoc"
                           type="border" size="small" color="primary" class="ml-2">Print</vs-button>

              </div>
        </div>
      </div>
    </span>
    </vx-card>

    <vx-card class="mt-8 text-danger font-bold" v-if="formulation.work_orders_in_progress.length > 0">
      The following work order(s) are currently in progress. Formulation ingredient updates are restricted until all in-progress work orders are completed. <br>
      <span class="text-danger ml-4" v-for="(item, index) in formulation.work_orders_in_progress" :key="index">
        <span class="cursor-pointer hover:text-primary" @click.stop="onClickViewWorkOrder(item.id)">{{ index + 1 }} - {{ item.work_order_code }} </span>
      </span>
    </vx-card>


    <vx-card class="mt-8" >
      <vs-tabs :value="active_step">
        <vs-tab label="Items">
          <div class="tab-text" >
            <formulation-item-tab
              v-on:refreshGeneral="refreshData"
              :isProductionFormulation="isProductionFormulation"
              :isFullyDilCodedFormulation="isFullyDilCodedFormulation"
              :linked_formulations_to_dilution="linked_formulations_to_dilution"
              :status_name="formulation.status.name"
              :formulationPermissions="formulationPermissions"
              :formulation_type="formulation.formulation_type"/>
          </div>
        </vs-tab>
        <vs-tab v-if="!isDilCodedFormulation" label="QC Section">
          <div class="tab-text">
            <formulation-qc-test-guide-tab
              ref="formulationQcSectionTab"
              v-bind:formulation_data="formulation"
              :formulationPermissions="['qc-elements-edit']"
              v-on:refreshGeneral="refreshData"/>
          </div>
        </vs-tab>
<!--        <vs-tab label="Test Status">-->
<!--          <div class="tab-text">-->
<!--            <formulation-qa-tab-->
<!--              v-bind:formulation_data="formulation"-->
<!--              v-on:refreshGeneral="refreshData"/>-->
<!--          </div>-->
<!--        </vs-tab>-->
<!--        <vs-tab label="QC Test">-->
<!--          <div class="tab-text">-->
<!--            <formulation-qc-test-tab-->
<!--              ref="formulationQcTestTab"-->
<!--              v-bind:formulation_data="formulation"-->
<!--              :formulationPermissions="formulationPermissions"-->
<!--              v-on:refreshGeneral="refreshData"/>-->
<!--          </div>-->
<!--        </vs-tab>-->
        <vs-tab label="History">
          <div class="tab-text">
            <formulation-activity-log-tab ref="formulationActivityLogTab"/>
          </div>
        </vs-tab>
        <vs-tab label="Applications">
          <div class="tab-text">
            <formulation-applications-tab
              v-on:refreshGeneral="refreshData"
              :formulationPermissions="formulationPermissions"/>
          </div>
        </vs-tab>
        <vs-tab label="Documents">
          <div class="tab-text">
            <formulation-product-catalogue-tab
              v-bind:formulationId="formulationId"
              :formulationPermissions="['qc-elements-edit']"
              v-bind:product_catalogue_info="formulation.documents"/>
          </div>
        </vs-tab>
        <vs-tab label="Dangerous Goods Note">
          <div class="tab-text">
            <formulation-dangerous-good-note-tab
              v-bind:formulation_data="formulation"
              :formulationPermissions="formulationPermissions"
              v-on:refreshGeneral="refreshData"/>
          </div>
        </vs-tab>
        <vs-tab label="Product Specification" v-if="formulation.product_specification">
          <div class="tab-text">
            <formulation-product-specification-tab
              :formulationPermissions="formulationPermissions"
              v-bind:formulation_data="formulation"
              :isProductionFormulation="isProductionFormulation"
              :isFullyDilCodedFormulation="isFullyDilCodedFormulation"
              v-on:refreshGeneral="refreshData"/>
          </div>
        </vs-tab>
        <vs-tab label="Provisional Product Specification" v-if="!isProductionFormulation">
          <div class="tab-text">
            <formulation-provisional-product-specification-tab
              :formulationPermissions="formulationPermissions"
              v-bind:formulation_data="formulation"
              v-on:refreshGeneral="refreshData"/>
          </div>
        </vs-tab>
        <vs-tab label="Manufacturing & Financials">
          <div class="tab-text">
            <formulation-manufacturing-and-financial-tab
              v-bind:formulation_data="formulation"
              :formulationPermissions="formulationPermissions"
              v-on:refreshGeneral="refreshData"/>
          </div>
        </vs-tab>
        <vs-tab label="Customer Related Data">
          <div class="tab-text">
            <formulation-customer-related-data-tab
              v-bind:formulation_data="formulation"
              :formulationPermissions="formulationPermissions"
              v-on:refreshGeneral="refreshData"/>
          </div>
        </vs-tab>
        <vs-tab label="Notes">
          <div class="tab-text">
            <formulation-notes-tab
              v-bind:formulation_data="formulation"
              :formulationPermissions="formulationPermissions"
              v-on:refreshGeneral="refreshData"/>
          </div>
        </vs-tab>
        <vs-tab label="Production Formulations" v-if="!isProductionFormulation">
          <div class="tab-text">
            <production-formulation-list-tab
              v-bind:formulation_list="formulation.linked_formulations"/>
          </div>
        </vs-tab>
        <vs-tab label="Formulations" v-if="isDilCodedFormulation">
          <div class="tab-text">
            <used-in-dilution-formulations-list-tab
              :linked_formulations_to_dilution="linked_formulations_to_dilution"
            />
          </div>
        </vs-tab>
      </vs-tabs>

    </vx-card>
    <approve-formulation-pop-up ref="approveFormulationPopUp"/>
    <qa-approve-formulation-pop-up ref="qaApproveFormulationPopUp"/>
    <qa-reject-formulation-pop-up ref="qaRejectFormulationPopUp"/>
    <batch-weight-update-formulation-pop-up ref="batchWeightUpdateFormulationPopUp"/>
    <view-customer-project-brief-pop-up ref="viewCustomerProjectBriefPopUp"/>
    <replica-formulation-pop-up ref="replicaFormulationPopUp"/>
  </div>
</template>

<script>
import {RepositoryFactory} from '@/repository/RepositoryFactory'

const FormulationsRepository = RepositoryFactory.get('formulations')
const SageSettingsRepository = RepositoryFactory.get('sage_settings');
const SupplierRawMaterialRepository = RepositoryFactory.get('supplierRawMaterial')
import FormulationItemTab from './FormulationItemTab.vue'
import FormulationQaTab from './FormulationQaTab.vue'
import FormulationProductCatalogueTab from './FormulationProductCatalogueTab.vue'
import FormulationActivityLogTab from './FormulationActivityLogTab.vue'
import FormulationQcTestTab from './FormulationQcTestTab.vue'
import FormulationApplicationsTab from './FormulationApplicationsTab.vue'
import FormulationDangerousGoodNoteTab from './FormulationDangerousGoodNoteTab.vue'
import FormulationQcTestGuideTab from "@/views/pages/formulations/FormulationQcTestGuideTab";
import FormulationProductSpecificationTab from "@/views/pages/formulations/FormulationProductSpecificationTab";
import FormulationManufacturingAndFinancialTab
  from "@/views/pages/formulations/FormulationManufacturingAndFinancialTab";
import ApproveFormulationPopUp from '@/components/popups/formulations/ApproveFormulationPopUp'
import QaApproveFormulationPopUp from '@/components/popups/formulations/QaApproveFormulationPopUp'
import QaRejectFormulationPopUp from '@/components/popups/formulations/QaRejectFormulationPopUp'
import BatchWeightUpdateFormulationPopUp from '@/components/popups/formulations/BatchWeightUpdateFormulationPopUp'
import ReplicaFormulationPopUp from "@/components/popups/formulations/ReplicaFormulationPopUp";

import ViewCustomerProjectBriefPopUp from '@/components/popups/customer/project-brief/ViewCustomerProjectBriefPopUp'

import SuccessMessage from '@/mixins/SuccessMessage'
import ErrorMessages from '@/mixins/ErrorMessages'
import moment from 'moment/src/moment'
import FormulationCustomerRelatedDataTab from "@/views/pages/formulations/FormulationCustomerRelatedDataTab";
import FormulationNotesTab from "@/views/pages/formulations/FormulationNotesTab";
import FormulationProvisionalProductSpecificationTab from "@/views/pages/formulations/FormulationProvisionalProductSpecificationTab";
import ProductionFormulationListTab from './ProductionFormulationListTab.vue';
import UsedInDilutionFormulationsListTab from './UsedInDilutionFormulationsListTab.vue';

import vSelect from "vue-select";

export default {
  name: "FormulationGeneral",
  data() {
    return {
      formulationId: '',
      formulation: {
        formulation_code: '',
        name: '',
        status: {},
        work_orders_in_progress: []
      },
      active_step: 0,
      openCreatePopUp: false,
      sage_nominal_codes:[],
      sage_departments: [],
      dropdown_options: [
        { key: 'N/A', value: 'n/a' }, 
        { key: 'Suitable', value: 'suitable'}, 
        { key: 'Certified', value: 'certified' }, 
        { key: 'Not Suitable', value: 'not-suitable' }
      ],
      dropdown_options_vegan: [
        { key: 'N/A', value: 'n/a' }, 
        { key: 'Suitable', value: 'suitable'}, 
        { key: 'Not Suitable', value: 'not-suitable' }
      ],
      linked_formulations_to_dilution: [],
      productionCodes: [],
      selectedRmCodePaired: null,
      originalFormulationData: null,
    }
  },
  components: {
    FormulationItemTab,
    FormulationQaTab,
    FormulationActivityLogTab,
    FormulationQcTestTab,
    FormulationApplicationsTab,
    ApproveFormulationPopUp,
    QaApproveFormulationPopUp,
    QaRejectFormulationPopUp,
    BatchWeightUpdateFormulationPopUp,
    ViewCustomerProjectBriefPopUp,
    ReplicaFormulationPopUp,
    FormulationProductCatalogueTab,
    FormulationDangerousGoodNoteTab,
    FormulationQcTestGuideTab,
    FormulationProductSpecificationTab,
    FormulationManufacturingAndFinancialTab,
    FormulationCustomerRelatedDataTab,
    FormulationNotesTab,
    FormulationProvisionalProductSpecificationTab,
    ProductionFormulationListTab,
    UsedInDilutionFormulationsListTab,
    'v-select': vSelect,
  },
  methods: {
    async refreshData() {
      this.loadFormulations()
    },
    async loadFormulations() {
      this.$vs.loading()
      const {data} = await FormulationsRepository.details(this.formulationId)
      this.formulation = data.data
      this.formulation.sage_nominal_code_id = data.data.sage_nominal_code.id
      this.formulation.sage_department_id = data.data.sage_department.id || null
      if (data.data.rm_code_paired) {
        const rm = data.data.rm_code_paired
        this.selectedRmCodePaired = { ...rm, category: { prefix: rm.category_prefix } }
      } else {
        this.selectedRmCodePaired = null
      }

      if (this.formulation.formulation_items.length > 0) {
        this.formulation.raw_material_items = this.formulation.raw_material_items.concat(this.formulation.formulation_items)
      }

      this.formulation.raw_material_items = this.formulation.raw_material_items.map((x) => {
        x.cost = Number(x.cost)
        x.quantity = Number(x.quantity)
        return x
      })
      if(this.formulation.formulation_type === 'spray dry powder' && this.formulation.spray_dry_carriers.length > 0) {
        const spray_dry_carriers = this.formulation.spray_dry_carriers.map((x) => {
          x.cost = Number(x.price)
          x.quantity = Number(x.quantity)
          return x
        })
        this.formulation.raw_material_items = this.formulation.raw_material_items.concat(spray_dry_carriers)
      }

      this.originalFormulationData = {
        name: this.formulation.name,
        legal_name: this.formulation.legal_name,
        alternative_legal_name: this.formulation.alternative_legal_name,
        batch_weight: this.formulation.batch_weight,
        notes: this.formulation.notes,
        formulation_type: this.formulation.formulation_type,
        halal: this.formulation.halal,
        kosher: this.formulation.kosher,
        organic: this.formulation.organic,
        vegan: this.formulation.vegan,
        sage_nominal_code_id: this.formulation.sage_nominal_code_id,
        sage_department_id: this.formulation.sage_department_id,
        is_thick_liquid: this.formulation.is_thick_liquid,
        rm_code_paired_id: this.selectedRmCodePaired ? this.selectedRmCodePaired.id : null,
      }

      this.$vs.loading.close()
    },
    async getSageNominalCodes(){
      if(this.$can('view-sage-nominal-code')) {
        this.$vs.loading()
        const {data} = await SageSettingsRepository.getListOfSageNominalCodes('','','',0)
        this.sage_nominal_codes =  data.data
        this.$vs.loading.close()
      }
    },
    async getSageDepartments(){
      if(this.$can('view-sage-nominal-code')) {
        this.$vs.loading()
        const {data} = await SageSettingsRepository.getListOfSageDepartments('','','',0)
        this.sage_departments =  data.data
        this.$vs.loading.close()
      }
    },
    async onClickStartQcTest() {
      this.active_step = 1
      setTimeout(async () => {
        await this.$refs.formulationQcSectionTab.startQcTest()
      }, 2500);
    },
    async onClickFormulationApproveConfirm() {
      const isValid = await this.$refs.form.validate();
      if (!isValid) {
        this.showErrorMessage('Please complete the missing compulsory fields')
      } else if(this.formulation.application_items.length === 0) {
        this.$vs.dialog({
          type: 'confirm',
          color: 'warning',
          title: 'Confirm',
          text: 'You have not added any applications to this formulation. Proceed?',
          acceptText: 'Confirm',
          accept: this.onClickFormulationApprove,
        })
      } else {
        await this.onClickFormulationApprove()
      }
    },
    async onClickFormulationApprove() {
      await this.onClickUpdateFormulation()
      await this.$refs.approveFormulationPopUp.openPopup(this.formulationId, this.formulation.name, this.formulation.formulation_type)
    },
    async openFormulationQaApprove() {
      await this.$refs.qaApproveFormulationPopUp.openPopup(this.formulationId)
    },
    async openFormulationQaReject() {
      await this.$refs.qaRejectFormulationPopUp.openPopup(this.formulationId)
    },
    async onClickUpdateFormulaBatchWeight() {
      await this.$refs.batchWeightUpdateFormulationPopUp.openPopup(this.formulationId)
    },
    async onClickViewReferenceFormula() {
      this.$router.replace({path: `/pages/formulations/${this.formulation.reference_formulation.id}`}, function () {
        location.reload()
      })
    },
    onClickViewRmCodePaired() {
      if (!this.selectedRmCodePaired) return
      const route = this.$router.resolve({
        path: `/pages/manage-nimble/suppliers/${this.selectedRmCodePaired.supplier_id}/raw-material/${this.selectedRmCodePaired.id}`
      })
      window.open(route.href, '_blank')
    },
    async onClickViewProjectBrief() {
      await this.$refs.viewCustomerProjectBriefPopUp.openPopup(this.formulation.customer_project_brief_id)
      // this.$router.push({path: `/pages/project-brief?open=${this.formulation.customer_project_brief_id}`})
    },
    printDoc () {
      let routeData = this.$router.resolve({name: 'formulation-print-view', params: {id: this.formulationId }});
      window.open(routeData.href, '_blank');
    },
    openFormulationSuspendConfirm() {
      this.$vs.dialog({
        type: 'confirm',
        color: 'warning',
        title: 'Confirm',
        text: 'Are you sure you want to make this change?',
        accept: this.onClickSuspendFormulations
      })
    },
    openFormulationReInstateConfirm() {
      this.$vs.dialog({
        type: 'confirm',
        color: 'primary',
        title: 'Confirm',
        text: 'Are you sure you want to make this change?',
        accept: this.onClickReInstateFormulations
      })
    },
    openFormulationChangeToDevConfirm() {
      this.$vs.dialog({
        type: 'confirm',
        color: 'primary',
        title: 'Confirm',
        text: 'Are you sure you want to make this change?',
        accept: this.onClickChangeToDevFormulations
      })
    },
    openFormulationQaSubmissionConfirm() {
      this.$vs.dialog({
        type: 'confirm',
        color: 'primary',
        title: 'Confirm',
        text: 'Are you sure you want to make this change?',
        accept: this.onClickQaSubmissionFormulations
      })
    },
    openFormulationArchiveConfirm() {
      this.$vs.dialog({
        type: 'confirm',
        color: 'danger',
        title: 'Confirm',
        text: 'Are you sure you want to archive this formulation?',
        accept: this.onClickArchiveFormulations
      })
    },
    openFormulationUnArchiveConfirm() {
      this.$vs.dialog({
        type: 'confirm',
        color: 'primary',
        title: 'Confirm',
        text: 'Are you sure you want to un-archive this formulation?',
        accept: this.onClickUnArchiveFormulations
      })
    },

    async onClickArchiveFormulations() {
      this.$vs.loading()
      try {
        await FormulationsRepository.archiveFormulations(this.formulationId)
        this.showSuccessMessage('Selected formulation archived')
        this.$router.push('/pages/formulations')
      } catch (e) {
        this.convertAndNotifyError(e)
      }
      this.$vs.loading.close()
    },
    async onClickUnArchiveFormulations() {
      this.$vs.loading()
      try {
        await FormulationsRepository.unArchiveFormulations(this.formulationId)
        this.showSuccessMessage('Selected formulation un-archived')
        await this.loadFormulations()
      } catch (e) {
        this.convertAndNotifyError(e)
      }
      this.$vs.loading.close()
    },

    async onClickSuspendFormulations() {
      this.$vs.loading()
      try {
        const data = (await FormulationsRepository.suspendFormulations(this.formulationId))
        this.showSuccessMessage(`Selected formulation suspended`);
        await this.loadFormulations()
      } catch (e) {
        this.convertAndNotifyError(e)
      }
      this.$vs.loading.close()
    },
    async onClickReInstateFormulations() {
      this.$vs.loading()
      try {
        const data = (await FormulationsRepository.reInstateFormulations(this.formulationId))
        this.showSuccessMessage(`Selected formulation re-instated`);
        await this.loadFormulations()
      } catch (e) {
        this.convertAndNotifyError(e)
      }
      this.$vs.loading.close()
    },
    async onClickChangeToDevFormulations() {
      this.$vs.loading()
      try {
        const data = (await FormulationsRepository.changeToDevFormulations(this.formulationId))
        this.showSuccessMessage(`Selected formulation change to development`);
        location.reload();
      } catch (e) {
        this.convertAndNotifyError(e)
      }
      this.$vs.loading.close()
    },
    async onClickQaSubmissionFormulations() {
      this.$vs.loading()
      try {
        const data = (await FormulationsRepository.qaSubmissionFormulations(this.formulationId))
        this.showSuccessMessage(`Selected formulation submitted to QA`);
        await this.loadFormulations()
      } catch (e) {
        this.convertAndNotifyError(e)
      }
      this.$vs.loading.close()
    },
    calMpPerKg() {
      return '';
    },
    async onChangePrivateFormulation() {
      this.$vs.loading()
      try {
        const data = (await FormulationsRepository.changeFormulationPrivate(this.formulationId))
        // this.showSuccessMessage(``);
        await this.loadFormulations()
      } catch (e) {
        this.formulation.is_private = !this.formulation.is_private
        this.convertAndNotifyError(e)
      }
      this.$vs.loading.close()
    },
    async onClickFormulationReplica() {
      await this.$refs.replicaFormulationPopUp.openPopup(this.formulationId)
    },
    onClickSamplePlay() {
      this.$router.push(`/pages/formulations/${this.formulationId}/sample`)
    },
    onlyNpdNotesChanged() {
      if (!this.originalFormulationData) return false
      const orig = this.originalFormulationData
      const f = this.formulation
      const currentRmCodePairedId = this.selectedRmCodePaired ? this.selectedRmCodePaired.id : null
      return (
        f.name === orig.name &&
        f.legal_name === orig.legal_name &&
        String(f.batch_weight) === String(orig.batch_weight) &&
        f.alternative_legal_name === orig.alternative_legal_name &&
        f.notes === orig.notes &&
        f.formulation_type === orig.formulation_type &&
        f.halal === orig.halal &&
        f.kosher === orig.kosher &&
        f.organic === orig.organic &&
        f.vegan === orig.vegan &&
        f.sage_nominal_code_id === orig.sage_nominal_code_id &&
        f.sage_department_id === orig.sage_department_id &&
        f.is_thick_liquid === orig.is_thick_liquid &&
        currentRmCodePairedId === orig.rm_code_paired_id
      )
    },
    async onclickFormulationSaveConfirmation() {
        try {
          if(this.onlyNpdNotesChanged()) {
            await this.onClickUpdateFormulation()
          } else if(this.isProductionFormulation && this.formulation.status.name === 'Production' && !this.isFullyDilCodedFormulation) {
              this.$vs.dialog({
              type: 'confirm',
              color: 'warning',
              title: 'Warning',
              text: 'You are changing a primary data field and that will change the status of this formulation ‘pre production’. You cannot make this product whilst its in pre-production status. Are you sure you wish to make this change?',
              acceptText: 'Confirm',
              accept: this.onClickUpdateFormulation,
              cancel: this.refreshData
            })
          } else if(this.isFullyDilCodedFormulation && this.linked_formulations_to_dilution.length > 0) {
            const formulation_names = this.linked_formulations_to_dilution.map(f => `${f.name || ''} (${f.code || ''})`).join(', ');
            this.$vs.dialog({
              type: 'confirm',
              color: 'warning',
              title: 'Warning',
              text: `( ${formulation_names} ) use this DIL code and will be suspended until QC reinstated. Are you sure you wish to make this change?`,
              acceptText: 'Confirm',
              accept: this.onClickUpdateFormulation,
              cancel: this.refreshData
            })
          } else {
            await this.onClickUpdateFormulation()
          }
        } catch (error) {
          this.convertAndNotifyError(error);
        }
    },
    async onClickUpdateFormulation() {
      await this.$vs.loading({
        container: '#formulation_general_details_section',
        scale: 0.45
      })
      try {
        const payload = {
          name: this.formulation.name,
          legal_name: this.formulation.legal_name,
          alternative_legal_name: this.formulation.alternative_legal_name,
          batch_weight: this.formulation.batch_weight,
          notes: this.formulation.notes,
          formulation_type: this.formulation.formulation_type,
          halal: this.formulation.halal,
          kosher: this.formulation.kosher,
          organic: this.formulation.organic,
          vegan: this.formulation.vegan,
          npd_notes: this.formulation.npd_notes,
          sage_nominal_code_id: this.formulation.sage_nominal_code_id,
          sage_department_id: this.formulation.sage_department_id,
          is_thick_liquid: this.formulation.is_thick_liquid,
          // classification: this.formulation.classification,
          rm_code_paired_id: this.selectedRmCodePaired ? this.selectedRmCodePaired.id : null,
        }
        await FormulationsRepository.updateFormulation(this.formulationId, payload)
        await this.refreshData()
        this.$refs.formulationActivityLogTab.loadFormulationsActivityLog()
        this.showSuccessMessage('Formulation Updated successfully')
      } catch (e) {
        this.convertAndNotifyError(e)
      }
      this.$vs.loading.close('#formulation_general_details_section > .con-vs-loading')
    },
    onClickCopyFormulationName(code) {
      navigator.clipboard.writeText(code);
      this.showSuccessMessage('Formulation code copied to clipboard')
    },
    formulation_update: _.debounce( async (vm) => {
      await vm.onClickUpdateFormulation()
    }, 1500),
    async onTypeForm() {
      this.formulation_update(this)
    },
    onClickViewWorkOrder(wo_id) {
      const route = this.$router.resolve({ path: `/pages/manufacturing/work-orders/${wo_id}/details` });
      window.open(route.href, '_blank');
    },
    async fetchProductionCodes(search = '') {
      try {
        const { data } = await SupplierRawMaterialRepository.getRawMaterialAcceptedList(1, `filter[search]=${search}`, 50, '')
        this.productionCodes = data.data
      } catch (e) {}
    },
    async fetchFormulationsAttachedToDilution() {
      try {
        if(this.isFullyDilCodedFormulation) {
          const { data } = (await FormulationsRepository.getFormulationsBasedOnDilution(this.formulationId)).data
          this.linked_formulations_to_dilution = data
        }
      } catch (error) {
        
      }
    }
  },
  async mounted() {
    this.$emit('setAppClasses', 'manage-formulation-page')
    this.formulationId = this.$route.params.id
    await this.loadFormulations()
    await this.getSageNominalCodes()
    await this.getSageDepartments()
    await this.fetchFormulationsAttachedToDilution()
    await this.fetchProductionCodes()
  },
  computed: {
    approveButtonValidations() {
      return this.formulation.status_id === 1 && this.formulation.formulation_code.includes('DEV-5')
    },
    reInstateButtonValidations() {
      return ( this.formulation.qa_status_id === 4 && this.formulation.status_id === 2 ) || this.formulation.status_id === 4 || (!this.formulation.formulation_code.includes('DEV-5') && this.formulation.status_id === 1)
    },
    isShowBaseLine() {
      return !this.formulation.formulation_code.includes('DEV-5')
    },
    isShowCopiedFormulation() {
      return this.formulation.formulation_code.includes('DEV-5') &&
        this.formulation.reference_formulation.formulation_code
    },
    isProductionFormulation: function () {
      return !this.formulation.formulation_code.includes('DEV-')
    },
    formulationPermissions: function () {
      if (this.isProductionFormulation) {
        return ['edit-production-formulations']
      } else {
        return ['edit-development-formulations']
      }
    },
    isDilCodedFormulation: function () {
      return this.formulation.formulation_code.includes('DIL-')
    },
    isFullyDilCodedFormulation: function () {
      return ['DIL-', 'EXT-', 'KEY-'].some(prefix => this.formulation.formulation_code.startsWith(prefix))
    },
  },
  mixins: [SuccessMessage, ErrorMessages],
}
</script>

<style lang="scss">

@media print {
  @page {
    size: A4 portrait;
  }
  .manage-formulation-page {
    *{
      visibility: hidden;
    }

    #formButtonSetDiv * {
      visibility: hidden !important;
    }

    #content-area {
      margin: 0 !important;
    }

    .vs-con-table {
      .vs-con-tbody {
        overflow: hidden !important;
      }
    }

    #formPrintDiv
    * {
      visibility: visible !important;
    }
    #formPrintDiv {
      position: relative;
      left: 0;
      top: 0;
      box-shadow: none;
    }

    #printTableDiv * {
      visibility: visible !important;
    }

    #printTableDiv {
      left: 0;
      top: 0;
    }



  }

}

</style>
